import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'spiral', label: 'Spiral Matrix' },
  { key: 'rotate', label: 'Rotate Image' },
  { key: 'setzeroes', label: 'Set Matrix Zeroes' },
  { key: 'search2d', label: 'Search a 2D Matrix' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

const GridBoxes = ({ label, grid, getCellState }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--muted)', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, overflowX: 'auto' }}>
      {grid.map((row, r) => (
        <div key={r} style={{ display: 'flex', gap: 4 }}>
          {row.map((cell, c) => {
            const st = getCellState ? getCellState(r, c) : {};
            return (
              <div key={c} style={{
                width: 42, height: 42, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                fontSize: 13, fontWeight: 800, fontFamily: "'Inter',sans-serif",
                background: st.bg || 'var(--bg3)', color: st.color || 'var(--text)',
                border: `1.5px solid ${st.border || 'var(--border)'}`, transition: 'all 0.2s ease',
              }}>{cell === null || cell === undefined ? '·' : cell}</div>
            );
          })}
        </div>
      ))}
    </div>
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={{
      padding: '8px 14px', borderRadius: 9, cursor: step === 0 ? 'default' : 'pointer',
      border: `1.5px solid ${step === 0 ? 'var(--border)' : V1}`, background: 'transparent',
      color: step === 0 ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
      fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: step === 0 ? 0.6 : 1,
    }}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

const parseGrid = str => str.split(';').map(row => row.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v))).filter(r => r.length > 0);
const getCellStateFactory = cur => (r, c) => {
  if (!cur || !cur.cell) return {};
  if (cur.cell[0] === r && cur.cell[1] === c) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
  return {};
};

/* ─── Spiral Matrix ───────────────────────────────────────────────── */
const SpiralMode = () => {
  const [input, setInput] = useState('1,2,3;4,5,6;7,8,9');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const grid = parseGrid(input);
    if (grid.length === 0 || grid.length > 6 || grid[0].length > 6) return;
    const m = grid.length, n = grid[0].length;
    const built = [];
    const order = [];
    let top = 0, bottom = m - 1, left = 0, right = n - 1;
    while (top <= bottom && left <= right) {
      for (let j = left; j <= right; j++) { order.push(grid[top][j]); built.push({ cell: [top, j], order: [...order], note: `Read [${top}][${j}] = ${grid[top][j]} (top row, left→right).` }); }
      top++;
      for (let i = top; i <= bottom; i++) { order.push(grid[i][right]); built.push({ cell: [i, right], order: [...order], note: `Read [${i}][${right}] = ${grid[i][right]} (right column, top→bottom).` }); }
      right--;
      if (top <= bottom) {
        for (let j = right; j >= left; j--) { order.push(grid[bottom][j]); built.push({ cell: [bottom, j], order: [...order], note: `Read [${bottom}][${j}] = ${grid[bottom][j]} (bottom row, right→left).` }); }
        bottom--;
      }
      if (left <= right) {
        for (let i = bottom; i >= top; i--) { order.push(grid[i][left]); built.push({ cell: [i, left], order: [...order], note: `Read [${i}][${left}] = ${grid[i][left]} (left column, bottom→top).` }); }
        left++;
      }
    }
    built.push({ done: true, order, note: `Final spiral order: [${order.join(', ')}].` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const grid = parseGrid(input);

  const code = {
    lines: [
      'List<Integer> spiralOrder(int[][] matrix) {',
      '    int top = 0, bottom = matrix.length - 1;',
      '    int left = 0, right = matrix[0].length - 1;',
      '    List<Integer> result = new ArrayList<>();',
      '    while (top <= bottom && left <= right) {',
      '        for (int j = left; j <= right; j++) result.add(matrix[top][j]);',
      '        top++;',
      '        for (int i = top; i <= bottom; i++) result.add(matrix[i][right]);',
      '        right--;',
      '        if (top <= bottom) {',
      '            for (int j = right; j >= left; j--) result.add(matrix[bottom][j]);',
      '            bottom--;',
      '        }',
      '        if (left <= right) {',
      '            for (int i = bottom; i >= top; i--) result.add(matrix[i][left]);',
      '            left++;',
      '        }',
      '    }',
      '    return result;',
      '}',
    ],
    active: -1,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GridBoxes label="matrix" grid={grid} getCellState={getCellStateFactory(cur)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1,2,3;4,5,6;7,8,9" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a grid (up to 6×6, rows separated by ";"), then Run.` : cur.note}
          {cur && cur.order && <> <Chip color={GREEN}>order: [{cur.order.join(', ')}]</Chip></>}
        </p>
      </div>
      <CodePanel title="Java — Spiral Matrix (Boundary Shrinking)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Rotate Image ────────────────────────────────────────────────── */
const RotateMode = () => {
  const [input, setInput] = useState('1,2,3;4,5,6;7,8,9');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const grid = parseGrid(input);
    if (grid.length === 0 || grid.length > 6 || grid.length !== grid[0].length) return;
    const n = grid.length;
    const m = grid.map(r => [...r]);
    const built = [{ grid: m.map(r => [...r]), note: `Start: original ${n}×${n} matrix.` }];
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const temp = m[i][j];
        m[i][j] = m[j][i];
        m[j][i] = temp;
        built.push({ grid: m.map(r => [...r]), cell: [i, j], note: `Transpose: swap [${i}][${j}] and [${j}][${i}].` });
      }
    }
    built.push({ grid: m.map(r => [...r]), note: `Transpose complete. Now reverse every row.` });
    for (let i = 0; i < n; i++) {
      m[i].reverse();
      built.push({ grid: m.map(r => [...r]), rowTouch: i, note: `Reverse row ${i}: [${m[i].join(', ')}].` });
    }
    built.push({ grid: m.map(r => [...r]), done: true, note: `Final: matrix rotated 90° clockwise, in place.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const grid = parseGrid(input);
  const getCellState = (r, c) => {
    if (!cur) return {};
    if (cur.cell && ((cur.cell[0] === r && cur.cell[1] === c) || (cur.cell[0] === c && cur.cell[1] === r))) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    if (cur.rowTouch === r) return { border: TEAL };
    return {};
  };

  const code = {
    lines: [
      'void rotate(int[][] matrix) {',
      '    int n = matrix.length;',
      '    for (int i = 0; i < n; i++) {',
      '        for (int j = i + 1; j < n; j++) {',
      '            int temp = matrix[i][j];',
      '            matrix[i][j] = matrix[j][i];',
      '            matrix[j][i] = temp;',
      '        }',
      '    }',
      '    for (int[] row : matrix) {',
      '        reverse(row);',
      '    }',
      '}',
    ],
    active: !cur ? -1 : cur.cell ? 5 : cur.rowTouch !== undefined ? 10 : -1,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GridBoxes label="matrix (transpose, then reverse each row)" grid={cur ? cur.grid : grid} getCellState={getCellState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1,2,3;4,5,6;7,8,9 (square, ≤6×6)" />
          <button onClick={run} style={smallBtn(V1)}>Rotate</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a SQUARE grid (up to 6×6), then Rotate.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Rotate Image (Transpose + Reverse)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Set Matrix Zeroes ───────────────────────────────────────────── */
const SetZeroesMode = () => {
  const [input, setInput] = useState('1,1,1;1,0,1;1,1,1');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const grid = parseGrid(input);
    if (grid.length === 0 || grid.length > 6 || grid[0].length > 6) return;
    const m = grid.map(r => [...r]);
    const rows = m.length, cols = m[0].length;
    const built = [{ grid: m.map(r => [...r]), note: `Start: find every 0 in the matrix, then mark its row and column in the first row/first column.` }];
    let firstRowZero = m[0].includes(0);
    let firstColZero = m.some(r => r[0] === 0);
    for (let i = 1; i < rows; i++) {
      for (let j = 1; j < cols; j++) {
        if (m[i][j] === 0) {
          m[i][0] = 0; m[0][j] = 0;
          built.push({ grid: m.map(r => [...r]), cell: [i, j], note: `Found a 0 at [${i}][${j}] — mark [${i}][0] and [0][${j}] as 0 (using the first row/column as markers).` });
        }
      }
    }
    for (let i = 1; i < rows; i++) {
      for (let j = 1; j < cols; j++) {
        if (m[i][0] === 0 || m[0][j] === 0) {
          m[i][j] = 0;
        }
      }
    }
    built.push({ grid: m.map(r => [...r]), note: `Using the markers, zero out every cell whose row or column marker is 0.` });
    if (firstRowZero) { for (let j = 0; j < cols; j++) m[0][j] = 0; }
    if (firstColZero) { for (let i = 0; i < rows; i++) m[i][0] = 0; }
    built.push({ grid: m.map(r => [...r]), done: true, note: `Finally, zero the first row/column themselves if they originally contained a 0. Final result shown.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const grid = parseGrid(input);
  const getCellState = (r, c) => {
    if (!cur || !cur.cell) return {};
    if (cur.cell[0] === r && cur.cell[1] === c) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
    return {};
  };

  const code = {
    lines: [
      'void setZeroes(int[][] matrix) {',
      '    int rows = matrix.length, cols = matrix[0].length;',
      '    boolean firstRowZero = false, firstColZero = false;',
      '    for (int j = 0; j < cols; j++) if (matrix[0][j] == 0) firstRowZero = true;',
      '    for (int i = 0; i < rows; i++) if (matrix[i][0] == 0) firstColZero = true;',
      '',
      '    for (int i = 1; i < rows; i++) {',
      '        for (int j = 1; j < cols; j++) {',
      '            if (matrix[i][j] == 0) { matrix[i][0] = 0; matrix[0][j] = 0; }',
      '        }',
      '    }',
      '    for (int i = 1; i < rows; i++) {',
      '        for (int j = 1; j < cols; j++) {',
      '            if (matrix[i][0] == 0 || matrix[0][j] == 0) matrix[i][j] = 0;',
      '        }',
      '    }',
      '    if (firstRowZero) for (int j = 0; j < cols; j++) matrix[0][j] = 0;',
      '    if (firstColZero) for (int i = 0; i < rows; i++) matrix[i][0] = 0;',
      '}',
    ],
    active: -1,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GridBoxes label="matrix" grid={cur ? cur.grid : grid} getCellState={getCellState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1,1,1;1,0,1;1,1,1" />
          <button onClick={run} style={smallBtn(V1)}>Run</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a grid (up to 6×6) with some 0s, then Run.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Set Matrix Zeroes (O(1) Space)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Search a 2D Matrix ──────────────────────────────────────────── */
const Search2DMode = () => {
  const [input, setInput] = useState('1,3,5,7;10,11,16,20;23,30,34,60');
  const [target, setTarget] = useState('3');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const grid = parseGrid(input);
    const t = Number(target);
    if (grid.length === 0 || grid[0].length === 0 || isNaN(t)) return;
    const m = grid.length, n = grid[0].length;
    const built = [];
    let lo = 0, hi = m * n - 1;
    let found = false;
    while (lo <= hi) {
      const mid = lo + Math.floor((hi - lo) / 2);
      const r = Math.floor(mid / n), c = mid % n;
      const val = grid[r][c];
      if (val === t) {
        built.push({ cell: [r, c], found: true, note: `mid → [${r}][${c}] = ${val}. Match! Found ${t}.` });
        found = true;
        break;
      } else if (val < t) {
        built.push({ cell: [r, c], note: `mid → [${r}][${c}] = ${val} < ${t} — search the right half. lo = ${mid + 1}.` });
        lo = mid + 1;
      } else {
        built.push({ cell: [r, c], note: `mid → [${r}][${c}] = ${val} > ${t} — search the left half. hi = ${mid - 1}.` });
        hi = mid - 1;
      }
    }
    if (!found) built.push({ done: true, note: `Search space exhausted — ${t} is not in the matrix.` });
    else built.push({ done: true, note: `Final answer: true.` });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const grid = parseGrid(input);
  const getCellState = (r, c) => {
    if (!cur || !cur.cell) return {};
    if (cur.cell[0] === r && cur.cell[1] === c) return { bg: cur.found ? `linear-gradient(135deg,${GREEN},#27AE60)` : `linear-gradient(135deg,${V1},${V2})`, border: cur.found ? GREEN : V1, color: '#fff' };
    return {};
  };

  const code = {
    lines: [
      'boolean searchMatrix(int[][] matrix, int target) {',
      '    int m = matrix.length, n = matrix[0].length;',
      '    int lo = 0, hi = m * n - 1;',
      '    while (lo <= hi) {',
      '        int mid = lo + (hi - lo) / 2;',
      '        int val = matrix[mid / n][mid % n];',
      '        if (val == target) return true;',
      '        if (val < target) lo = mid + 1;',
      '        else hi = mid - 1;',
      '    }',
      '    return false;',
      '}',
    ],
    active: -1,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GridBoxes label="matrix (sorted row-major)" grid={grid} getCellState={getCellState} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1,3,5,7;10,11,16,20" />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>target</span>
          <input value={target} onChange={e => { setTarget(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 60 }} />
          <button onClick={run} style={smallBtn(V1)}>Search</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a sorted matrix and a target, then Search — binary search over the flattened index space.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Search a 2D Matrix (Binary Search)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const n = 3 + Math.floor(Math.random() * 3);
  const r = Math.floor(Math.random() * n);
  const c = Math.floor(Math.random() * n);
  const newR = c, newC = n - 1 - r;
  return { n, r, c, correct: `[${newR}][${newC}]` };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const wrongOption = (() => {
    const alt = `[${scenario.n - 1 - scenario.r}][${scenario.c}]`;
    return alt === scenario.correct ? `[${scenario.c}][${scenario.r}]` : alt;
  })();
  const options = React.useMemo(() => (Math.random() < 0.5 ? [scenario.correct, wrongOption] : [wrongOption, scenario.correct]), [scenario]);

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '4px 0 14px', lineHeight: 1.6 }}>
        In an {scenario.n}×{scenario.n} matrix rotated 90° clockwise, where does the element originally at [{scenario.r}][{scenario.c}] end up?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map((opt, oi) => {
          const revealed = answer !== null;
          const isCorrect = opt === scenario.correct;
          const isPicked = opt === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={oi} onClick={() => pick(opt)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{opt}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text)' }}>Rule: [r][c] → [c][n-1-r] for a 90° clockwise rotation.</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const MatrixPlayground = () => {
  const [mode, setMode] = useState('spiral');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'spiral' && <SpiralMode />}
      {mode === 'rotate' && <RotateMode />}
      {mode === 'setzeroes' && <SetZeroesMode />}
      {mode === 'search2d' && <Search2DMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default MatrixPlayground;
